/**
 * DRR-092: Download Warning Dialog
 *
 * Mantine Modal that shows backend-generated warning text before
 * allowing download of legacy archive files.
 *
 * CRITICAL (DRR-055): Warning text comes from the backend
 * `warningMessage` field, NOT hardcoded here. The frontend
 * renders whatever the backend sends. If warning language needs
 * to change (legal review, regulatory update), it's a backend
 * change, not a frontend deploy.
 *
 * Two warning levels:
 * - STANDARD: Clean files — generic "from previous system" warning
 * - ELEVATED: Macro-detected files — prominent "active content" warning
 */

import { Modal, Text, Alert, Group, Button, Stack } from '@mantine/core';
import { IconAlertTriangle, IconDownload } from '@tabler/icons-react';
import type { WarningLevel, ScanStatus } from '@/generated/graphql';
import { ScanStatusBadge } from './ScanStatusBadge';

interface DownloadWarningDialogProps {
  opened: boolean;
  onClose: () => void;
  onConfirm: () => void;
  fileName: string;
  fileSize: number;
  scanStatus: ScanStatus;
  scanDate?: string | null;
  warningLevel: WarningLevel;
  /** Backend-generated warning message text (DRR-055: dumb frontend) */
  warningMessage: string;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function DownloadWarningDialog({
  opened,
  onClose,
  onConfirm,
  fileName,
  fileSize,
  scanStatus,
  scanDate,
  warningLevel,
  warningMessage,
}: DownloadWarningDialogProps) {
  const isElevated = warningLevel === 'ELEVATED';

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        isElevated
          ? 'WARNING: Active Content Detected'
          : 'Download Legacy File'
      }
      centered
      size="md"
    >
      <Stack gap="md">
        {/* Warning message from backend */}
        <Alert
          color={isElevated ? 'orange' : 'yellow'}
          icon={<IconAlertTriangle />}
          title={isElevated ? 'Macros Detected' : 'Legacy File'}
        >
          <Text size="sm">{warningMessage}</Text>
        </Alert>

        {/* File details */}
        <Alert variant="light" color="gray">
          <Group gap="sm">
            <ScanStatusBadge status={scanStatus} scanDate={scanDate} />
            <Text size="sm" fw={500}>
              {fileName}
            </Text>
            <Text size="sm" c="dimmed">
              ({formatFileSize(fileSize)})
            </Text>
          </Group>
        </Alert>

        {/* Actions */}
        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button
            color={isElevated ? 'orange' : 'blue'}
            leftSection={<IconDownload size={16} />}
            onClick={onConfirm}
          >
            Download Anyway
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
